// authedFetch：自动附带 Authorization: Bearer <accessToken> 的 fetch 包装。
//
// 行为：
//  - 请求前取有效 access token（必要时静默 refresh）；附加 Authorization 头。
//  - 响应 401 或本地判定 token 过期时：触发「重新认证」回调，并把**待提交请求**
//    （含完整 payload）登记到待办队列，登录成功后可重放（Req 11.4）。
//  - 空闲超时（Req 2.6）：超过阈值未活动则使会话失效并要求重认证。
//
// 设计依据：design.md「PWA 设计 → 认证 / 关键交互流程 1、7」。
// Requirements: 2.1, 2.2, 2.6, 11.4。

import type { CognitoAuth } from "./cognito.js";

/** 被保留的待提交请求（用于重认证后重放）。 */
export interface PendingRequest {
  readonly id: string;
  readonly input: string;
  readonly init?: RequestInit;
  /** 入队时间戳（epoch ms）。 */
  readonly queuedAt: number;
}

/** 重认证触发时的回调（通常用于跳转 Hosted UI 或弹出提示）。 */
export type ReauthHandler = (pending: readonly PendingRequest[]) => void;

export interface AuthedFetchOptions {
  /** 空闲超时（ms）。默认 24h（对应 Req 2.6 默认 24 小时）。 */
  readonly idleTimeoutMs?: number;
  /** 自定义时间源（便于测试）。 */
  readonly now?: () => number;
}

const DEFAULT_IDLE_TIMEOUT_MS = 24 * 60 * 60 * 1000;

/**
 * AuthedFetchClient：持有 CognitoAuth 与待办请求队列，负责附带 token、
 * 处理 401 / 过期、保留待提交请求并在重认证后重放。
 */
export class AuthedFetchClient {
  private readonly auth: CognitoAuth;
  private readonly idleTimeoutMs: number;
  private readonly now: () => number;
  private lastActivityAt: number;
  private pending: PendingRequest[] = [];
  private reauthHandler: ReauthHandler | null = null;
  private seq = 0;

  constructor(auth: CognitoAuth, opts: AuthedFetchOptions = {}) {
    this.auth = auth;
    this.idleTimeoutMs = opts.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;
    this.now = opts.now ?? (() => Date.now());
    this.lastActivityAt = this.now();
  }

  /** 注册重认证回调（App 层用它跳转登录并提示）。 */
  onReauthRequired(handler: ReauthHandler): void {
    this.reauthHandler = handler;
  }

  /** 待重放的请求快照。 */
  getPending(): readonly PendingRequest[] {
    return [...this.pending];
  }

  /** 标记一次用户活动（重置空闲计时）。 */
  markActivity(): void {
    this.lastActivityAt = this.now();
  }

  /** 是否已空闲超时（Req 2.6）。 */
  isIdleExpired(): boolean {
    return this.now() - this.lastActivityAt > this.idleTimeoutMs;
  }

  /**
   * 解析输入为绝对/相对 URL：相对路径基于 auth.config.apiBaseUrl 拼接。
   */
  private resolveUrl(input: string): string {
    if (/^https?:\/\//i.test(input)) return input;
    const base = this.auth.config.apiBaseUrl;
    if (!base) return input;
    return `${base}${input.startsWith("/") ? "" : "/"}${input}`;
  }

  /**
   * 带认证的 fetch。401 / 过期 / 空闲超时会触发重认证并保留该请求。
   * @param input 路径或完整 URL。
   * @param init 标准 RequestInit。
   * @param opts.preserveOnReauth 401 时是否保留该请求以便重放（默认 true）。
   */
  async fetch(
    input: string,
    init: RequestInit = {},
    opts: { preserveOnReauth?: boolean } = {},
  ): Promise<Response> {
    const preserve = opts.preserveOnReauth ?? true;

    // 空闲超时：使会话失效并要求重认证（Req 2.6）。
    if (this.isIdleExpired()) {
      this.auth.clearSession();
      if (preserve) this.preserve(input, init);
      this.triggerReauth();
      throw new SessionExpiredError("会话空闲超时，请重新登录");
    }

    const token = await this.auth.getValidAccessToken();
    if (!token) {
      // 无有效 token 且无法刷新 → 保留请求并要求重认证（Req 11.4）。
      if (preserve) this.preserve(input, init);
      this.triggerReauth();
      throw new SessionExpiredError("会话已过期，请重新登录");
    }

    this.markActivity();
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);

    const res = await fetch(this.resolveUrl(input), { ...init, headers });

    if (res.status === 401) {
      // 服务端拒绝：尝试刷新一次后重试；仍 401 则保留并要求重认证。
      const refreshed = await this.auth.refresh();
      if (refreshed?.accessToken) {
        const retryHeaders = new Headers(init.headers);
        retryHeaders.set("Authorization", `Bearer ${refreshed.accessToken}`);
        const retry = await fetch(this.resolveUrl(input), {
          ...init,
          headers: retryHeaders,
        });
        if (retry.status !== 401) {
          this.markActivity();
          return retry;
        }
      }
      this.auth.clearSession();
      if (preserve) this.preserve(input, init);
      this.triggerReauth();
      throw new SessionExpiredError("会话已过期（401），请重新登录");
    }

    this.markActivity();
    return res;
  }

  /** 登记一个待提交请求（保留 payload 以便重认证后重放）。 */
  private preserve(input: string, init: RequestInit): void {
    this.pending.push({
      id: `req-${++this.seq}`,
      input,
      init: cloneInit(init),
      queuedAt: this.now(),
    });
  }

  private triggerReauth(): void {
    this.reauthHandler?.(this.getPending());
  }

  /**
   * 重认证成功后重放全部待提交请求，返回各自结果。
   * 重放前重置空闲计时。
   */
  async replayPending(): Promise<Array<{ request: PendingRequest; response?: Response; error?: unknown }>> {
    this.markActivity();
    const toReplay = this.pending;
    this.pending = [];
    const results: Array<{ request: PendingRequest; response?: Response; error?: unknown }> = [];
    for (const req of toReplay) {
      try {
        const response = await this.fetch(req.input, req.init ?? {}, {
          preserveOnReauth: false,
        });
        results.push({ request: req, response });
      } catch (error) {
        results.push({ request: req, error });
      }
    }
    return results;
  }

  /** 丢弃所有待提交请求（用户放弃重放时）。 */
  clearPending(): void {
    this.pending = [];
  }
}

/** 会话过期 / 需重认证的专用错误类型，便于调用方区分处理。 */
export class SessionExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SessionExpiredError";
  }
}

// 浅克隆 RequestInit，保留可序列化 body（字符串）。
// 注意：流式 body（ReadableStream）不可重放，调用方应使用字符串/Blob body。
function cloneInit(init: RequestInit): RequestInit {
  const headers = init.headers
    ? Object.fromEntries(new Headers(init.headers).entries())
    : undefined;
  return { ...init, headers };
}
